
import React from "react";

// ========================================
// PRODUCT CASE STUDIES
// ========================================

const ProductCaseStudies = ({
    data = [],
    onChange
}) => {

    const caseStudies = Array.isArray(data)
        ? data
        : [];

    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";

    // ========================================
    // CASE STUDY MANAGEMENT
    // ========================================

    const addCaseStudy = () => {

        onChange([
            ...caseStudies,
            {
                title: "",
                industry: "",
                location: "",
                customerProblem: "",
                solution: "",
                outcome: "",
                mediaUrls: [],
                approvedForPublicUse: false
            }
        ]);

    };

    const updateCaseStudy = (
        index,
        field,
        value
    ) => {

        onChange(
            caseStudies.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        [field]: value
                    }
                    : item
            )
        );

    };

    const removeCaseStudy = (index) => {

        onChange(
            caseStudies.filter(
                (_, i) => i !== index
            )
        );

    };

    // ========================================
    // MEDIA URL MANAGEMENT
    // ========================================

    const addMediaUrl = (caseStudyIndex) => {

        const caseStudy = caseStudies[caseStudyIndex];

        updateCaseStudy(
            caseStudyIndex,
            "mediaUrls",
            [
                ...(caseStudy.mediaUrls || []),
                ""
            ]
        );

    };

    const updateMediaUrl = (
        caseStudyIndex,
        mediaIndex,
        value
    ) => {

        const caseStudy = caseStudies[caseStudyIndex];

        const updatedUrls = (
            caseStudy.mediaUrls || []
        ).map(
            (url, index) =>
                index === mediaIndex
                    ? value
                    : url
        );

        updateCaseStudy(
            caseStudyIndex,
            "mediaUrls",
            updatedUrls
        );

    };

    const removeMediaUrl = (
        caseStudyIndex,
        mediaIndex
    ) => {

        const caseStudy = caseStudies[caseStudyIndex];

        updateCaseStudy(
            caseStudyIndex,
            "mediaUrls",
            (caseStudy.mediaUrls || []).filter(
                (_, index) => index !== mediaIndex
            )
        );

    };

    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-6">

            {/* HEADER */}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                <div>

                    <h3 className="text-base font-semibold text-gray-900">
                        Product Case Studies
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                        Document real-world applications,
                        customer challenges, solutions and
                        outcomes associated with this product.
                    </p>

                </div>

                <button
                    type="button"
                    onClick={addCaseStudy}
                    className={addButtonClass}
                >
                    + Add Case Study
                </button>

            </div>

            {/* EMPTY STATE */}

            {caseStudies.length === 0 && (

                <div className="py-12 text-center border border-dashed rounded-lg">

                    <p className="text-sm text-gray-500">
                        No case studies added yet.
                    </p>

                    <p className="text-xs text-gray-400 mt-2">
                        Add documented product applications
                        and their outcomes.
                    </p>

                </div>

            )}

            {/* CASE STUDY LIST */}

            {caseStudies.map((caseStudy, caseStudyIndex) => (

                <div
                    key={caseStudy._id || caseStudyIndex}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                >

                    {/* HEADER */}

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-gray-100 px-5 py-4">

                        <div>

                            <h4 className="font-semibold text-gray-900">
                                Case Study {caseStudyIndex + 1}
                            </h4>

                            {caseStudy.title && (

                                <p className="text-xs text-gray-500 mt-1">
                                    {caseStudy.title}
                                </p>

                            )}

                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                removeCaseStudy(caseStudyIndex)
                            }
                            className={removeButtonClass}
                        >
                            Remove Case Study
                        </button>

                    </div>

                    {/* FIELDS */}

                    <div className="p-5 space-y-5">

                        {/* TITLE */}

                        <div>

                            <label className={labelClass}>
                                Case Study Title *
                            </label>

                            <input
                                type="text"
                                value={caseStudy.title || ""}
                                onChange={(event) =>
                                    updateCaseStudy(
                                        caseStudyIndex,
                                        "title",
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. Waterproofing Solution for a Commercial Building"
                                className={inputClass}
                            />

                        </div>

                        {/* INDUSTRY AND LOCATION */}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                            <div>

                                <label className={labelClass}>
                                    Industry
                                </label>

                                <input
                                    type="text"
                                    value={caseStudy.industry || ""}
                                    onChange={(event) =>
                                        updateCaseStudy(
                                            caseStudyIndex,
                                            "industry",
                                            event.target.value
                                        )
                                    }
                                    placeholder="e.g. Construction"
                                    className={inputClass}
                                />

                            </div>

                            <div>

                                <label className={labelClass}>
                                    Location
                                </label>

                                <input
                                    type="text"
                                    value={caseStudy.location || ""}
                                    onChange={(event) =>
                                        updateCaseStudy(
                                            caseStudyIndex,
                                            "location",
                                            event.target.value
                                        )
                                    }
                                    placeholder="e.g. Pune, Maharashtra"
                                    className={inputClass}
                                />

                            </div>

                        </div>

                        {/* CUSTOMER PROBLEM */}

                        <div>

                            <label className={labelClass}>
                                Customer Problem
                            </label>

                            <textarea
                                value={caseStudy.customerProblem || ""}
                                onChange={(event) =>
                                    updateCaseStudy(
                                        caseStudyIndex,
                                        "customerProblem",
                                        event.target.value
                                    )
                                }
                                placeholder="Describe the customer's original problem, site conditions and challenges."
                                rows={4}
                                className={inputClass}
                            />

                        </div>

                        {/* SOLUTION */}

                        <div>

                            <label className={labelClass}>
                                Solution Provided
                            </label>

                            <textarea
                                value={caseStudy.solution || ""}
                                onChange={(event) =>
                                    updateCaseStudy(
                                        caseStudyIndex,
                                        "solution",
                                        event.target.value
                                    )
                                }
                                placeholder="Describe the product solution, application approach and work performed."
                                rows={5}
                                className={inputClass}
                            />

                        </div>

                        {/* OUTCOME */}

                        <div>

                            <label className={labelClass}>
                                Outcome / Results
                            </label>

                            <textarea
                                value={caseStudy.outcome || ""}
                                onChange={(event) =>
                                    updateCaseStudy(
                                        caseStudyIndex,
                                        "outcome",
                                        event.target.value
                                    )
                                }
                                placeholder="Describe the actual documented results and customer outcomes."
                                rows={4}
                                className={inputClass}
                            />

                        </div>

                        {/* MEDIA URLS */}

                        <div className="border-t pt-5 space-y-4">

                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">

                                <div>

                                    <h5 className="text-sm font-semibold text-gray-800">
                                        Case Study Media
                                    </h5>

                                    <p className="text-xs text-gray-500 mt-1">
                                        Add URLs for project photographs,
                                        documents or supporting media.
                                    </p>

                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        addMediaUrl(caseStudyIndex)
                                    }
                                    className="text-sm text-blue-700 hover:underline"
                                >
                                    + Add Media URL
                                </button>

                            </div>

                            {(caseStudy.mediaUrls || []).length === 0 && (

                                <p className="text-xs text-gray-500">
                                    No media URLs added.
                                </p>

                            )}

                            {(caseStudy.mediaUrls || []).map(
                                (url, mediaIndex) => (

                                    <div
                                        key={mediaIndex}
                                        className="flex flex-col sm:flex-row gap-3"
                                    >

                                        <div className="flex-1">

                                            <input
                                                type="url"
                                                value={url || ""}
                                                onChange={(event) =>
                                                    updateMediaUrl(
                                                        caseStudyIndex,
                                                        mediaIndex,
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="https://example.com/project-image.jpg"
                                                className={inputClass}
                                            />

                                        </div>

                                        <div className="sm:self-end">

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeMediaUrl(
                                                        caseStudyIndex,
                                                        mediaIndex
                                                    )
                                                }
                                                className={removeButtonClass}
                                            >
                                                Remove
                                            </button>

                                        </div>

                                    </div>

                                )
                            )}

                        </div>

                        {/* PUBLIC APPROVAL */}

                        <div className="border-t pt-5">

                            <div className="border rounded-lg p-4 bg-gray-50">

                                <label className="flex items-start gap-3 cursor-pointer">

                                    <input
                                        type="checkbox"
                                        checked={
                                            caseStudy.approvedForPublicUse === true
                                        }
                                        onChange={(event) =>
                                            updateCaseStudy(
                                                caseStudyIndex,
                                                "approvedForPublicUse",
                                                event.target.checked
                                            )
                                        }
                                        className="mt-1 h-4 w-4"
                                    />

                                    <div>

                                        <span className="block text-sm font-medium text-gray-900">
                                            Approved for Public Use
                                        </span>

                                        <p className="text-xs text-gray-500 mt-1">
                                            Enable only when RAZON has
                                            approved this case study for
                                            use in customer-facing AI
                                            conversations and other
                                            public communications.
                                        </p>

                                    </div>

                                </label>

                            </div>

                        </div>

                    </div>

                </div>

            ))}

            {/* GUIDANCE */}

            <div className="border-t pt-4">

                <p className="text-xs text-gray-500">
                    Case studies should describe actual projects
                    and verified outcomes. Do not include
                    confidential customer information without
                    appropriate authorization. Only approved
                    case studies should be made available for
                    customer-facing AI responses.
                </p>

            </div>

        </div>

    );

};

export default ProductCaseStudies;
